import React, { useEffect, useState, useRef } from 'react';
import PillRenderer from 'common/molecules/pillRenderer/PillRenderer';
import SearchInputField from './SearchInputField';
import SearchInputResultsList from './SearchInputResultList';

const ResourceFetchingSearchInputField = ({
  isSingleValue,
  selectedOptions,
  setSelectedOptions,
  searchOptionsFetcher,
  resourceType,
  resultAttributeId,
  disableCustomInputs,
  inputTheme,
  iconOverride,
  hidePills,
  minQueryLength = 3,
  freeFormDisabled,
  queryOnInputFocus,
}) => {
  const [searchOptions, setSearchOptions] = useState([]);
  const [enterKeyState, setEnterKeyState] = useState('');
  const [initialValue, setInitialValue] = useState('');
  const inputRef = useRef(null);

  const createNewSelectedOptions = (alreadySelectedOptions, newOption) => (isSingleValue ? [newOption] : [...alreadySelectedOptions, newOption]);

  const isOptionSelected = (option) => selectedOptions.some((selected) => {
    // If both have institution_id and they match, consider them the same
    if (option?.institution_id && selected?.institution_id) {
      return option.institution_id === selected.institution_id;
    }

    // Otherwise, fall back to deep comparison
    const optionKeys = Object.keys(option).sort();
    const selectedKeys = Object.keys(selected).sort();

    // Different number of properties = different objects
    if (optionKeys.length !== selectedKeys.length) {
      return false;
    }

    // Check if all properties match
    return optionKeys.every((key) => option[key] === selected[key]);
  });

  useEffect(() => {
    if (freeFormDisabled) {
      const foundOption = searchOptions.find(
        (n) => n[resultAttributeId].toLowerCase() === enterKeyState.toLowerCase().trim(),
      );
      const foundOptionValue = foundOption?.[resultAttributeId];
      const foundOptionIsAlreadySelected = isOptionSelected(foundOption);

      if (!foundOption) {
        return;
      }

      if (!foundOptionIsAlreadySelected) {
        setSelectedOptions(
          createNewSelectedOptions(selectedOptions, foundOption),
        );
        inputRef.current.value = foundOptionValue;
      }

      setSearchOptions([]);
      return;
    }

    if (!enterKeyState) {
      return;
    }

    setSearchOptions([]);
    const enterKeyOption = { [resultAttributeId]: enterKeyState };
    if (!isOptionSelected(enterKeyOption)) {
      setSelectedOptions(createNewSelectedOptions(selectedOptions, enterKeyOption));
    }
  }, [enterKeyState, freeFormDisabled]);

  useEffect(() => {
    if (isSingleValue && selectedOptions?.length > 0) {
      const [singleOption] = selectedOptions;
      const value = singleOption[resultAttributeId];
      inputRef.current.value = value;
      setInitialValue(value);
    }
  }, []);

  const handleInputOnChange = (query = '') => {
    if (isSingleValue && query.length === 0 && (initialValue || selectedOptions?.length)) {
      setSelectedOptions([]);
    }

    if (query?.length >= minQueryLength) {
      searchOptionsFetcher(resourceType, query).then((results) => {
        if (inputRef?.current?.value.toLowerCase() === query.toLowerCase()) {
          setSearchOptions(results?.results || []);
        }
      });
    } else setSearchOptions([]);
  };

  const handleOptionSelect = (option) => {
    setSearchOptions([]);
    const resource = option[resultAttributeId];
    inputRef.current.value = isSingleValue ? resource : '';
    if (!isOptionSelected(option)) {
      setSelectedOptions(createNewSelectedOptions(selectedOptions, option));
    }
  };

  const handleInputKeyUp = (key, query) => {
    if (disableCustomInputs) return;
    if (key === 'Enter' && query) setEnterKeyState(query);
  };

  const handleUnselectPill = (option) => {
    if (isSingleValue) {
      setSelectedOptions(null);
    } else {
      const optionValue = option[resultAttributeId];
      setSelectedOptions(selectedOptions.filter((n) => n[resultAttributeId] !== optionValue));
    }
  };

  const showList = searchOptions?.length > 0;
  const showPills = !hidePills && selectedOptions?.length > 0;

  const handleOnFocus = () => {
    if (queryOnInputFocus) {
      if (!searchOptions?.length && !inputRef?.current?.value) {
        handleInputOnChange();
      }
    }
  };

  return (
    <SearchInputField
      iconOverride={iconOverride}
      handleInputChange={(e) => handleInputOnChange(e.target.value)}
      handleKeyUp={(e) => handleInputKeyUp(e.key, e.target.value)}
      inputTheme={inputTheme}
      renderSearchOptions={!!searchOptions?.length}
      searchOptions={searchOptions}
      handleSearchOptionSelect={handleOptionSelect}
      resultAttributeId={resultAttributeId}
      selectedOptions={selectedOptions}
      handlePillClick={(value) => handleUnselectPill(value)}
      inputRef={inputRef}
      handleOnFocus={handleOnFocus}
    >
      <>
        {showList && (
          <SearchInputResultsList
            searchOptions={searchOptions}
            handleSelect={handleOptionSelect}
            resultAttributeId={resultAttributeId}
          />
        )}

        {showPills && (
          <div style={{ marginLeft: '4px' }}>
            <PillRenderer
              items={selectedOptions?.map((option) => option[resultAttributeId]) || []}
              activeItems={selectedOptions?.map((option) => option[resultAttributeId]) || []}
              handlePillClick={(value) => {
                const option = selectedOptions.find((opt) => opt[resultAttributeId] === value);
                handleUnselectPill(option);
              }}
            />
          </div>
        )}
      </>
    </SearchInputField>
  );
};

export default ResourceFetchingSearchInputField;
